import { createFileRoute } from "@tanstack/react-router"
import {
  getPaginatedProductions,
  useGetMyLicensesSuspense,
} from "@/orval/gen/index.ts"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get.ts"
import { useBreakpoints } from "@/utils/use-breakpoints.tsx"
import { Page } from "@components/Page/index.tsx"
import { Stack, Group, Card, Highlight, Text, Checkbox } from "@mantine/core"
import { useTranslation } from "react-i18next"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group.tsx"
import { SearchTextInput } from "../../-components/search-text-input.tsx"
import { SeasonMultiSelect } from "../../-components/season-multiselect.tsx"
import { useUserLocalStorage } from "../../-components/use-user-local-storage.tsx"
import {
  InfiniteScrollingTable,
  SortingStateAndUpdater,
} from "@/routes/-components/custom-tanstack-table"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery.ts"
import { GenreTreeSelect } from "@components/tree-select/GenreTreeSelect.tsx"
import { SortingState } from "@tanstack/react-table"
import { useProductionTableColumns } from "./-components/use-production-table-columns.tsx"
import { DefaultInfiniteList } from "@components/InfiniteList/index.tsx"
import { DefaultListItem } from "@components/default-list/list-item.tsx"
import { getFormattedDate } from "@/utils/get-formatted-date.ts"

export interface Search {
  searchString?: string
  genreTreeNodes?: string[]
  seasonIds: number[]
  reminderDetailOpened?: boolean
  reminderId?: number
  sorting?: SortingState
  inactive?: boolean
}

export const Route = createFileRoute("/app/art/productions/")({
  component: ProductionsPage,
  validateSearch: (search: Search) => ({
    ...search,
    reminderDetailOpened: search.reminderDetailOpened,
    reminderId: search.reminderId,
    genreTreeNodes: search.genreTreeNodes ?? [],
  }),
})

export function ProductionsPage() {
  const { t } = useTranslation("app_server")
  const navigate = Route.useNavigate()
  const { data: seasonFilterDto } = useSuspenseQueryGet(
    "/api/Production/GetDefaultSeason",
    null
  )

  const { xs } = useBreakpoints()
  const { data: userLicenses } = useGetMyLicensesSuspense()
  const search = Route.useSearch()
  const { genreTreeNodes, seasonIds, searchString } = search

  const [storedSeasonIds, setStoredSeasonIds] = useUserLocalStorage<
    number[] | undefined
  >({
    key: "productions-season-filter",
    defaultValue: seasonFilterDto.defaultValue,
  })
  const [storedGenreTreeNodes, setStoredGenreTreeNodes] = useUserLocalStorage<
    string[] | undefined
  >({
    key: "productions-genre-filter",
    defaultValue: undefined,
  })
  const [showFilters, setShowFilters] = useUserLocalStorage<boolean>({
    key: "productions-show-filters",
    defaultValue: false,
  })
  const queryOptions = useResponsiveFilterQueryOptions(showFilters)
  React.useEffect(() => {
    updateSearch({
      genreTreeNodes: search.genreTreeNodes ?? storedGenreTreeNodes ?? [],
      seasonIds: search.seasonIds ?? storedSeasonIds ?? undefined,
      searchString: search.searchString,
    })
  }, [])

  React.useEffect(() => {
    if (storedGenreTreeNodes !== search.genreTreeNodes) {
      setStoredGenreTreeNodes(search.genreTreeNodes)
    }
  }, [search.genreTreeNodes, storedGenreTreeNodes, setStoredGenreTreeNodes])

  function navigateToProductionId(productionId: number) {
    navigate({
      to: "/app/art/productions/$id",
      params: { id: productionId.toString() },
      search: { tab: "start", seasonIds: storedSeasonIds ?? [] },
    })
  }

  function updateSearch(input: Partial<Search>) {
    navigate({
      search: {
        ...search,
        ...input,
      },
      replace: true,
    })
  }
  const sortingState: SortingStateAndUpdater = [
    search.sorting ?? [],
    (updater) =>
      typeof updater === "function"
        ? updateSearch({ sorting: updater(search.sorting ?? []) })
        : updateSearch({ sorting: updater }),
  ]
  const queryResult = useDefaultInfiniteQuery(
    getPaginatedProductions,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
      genreTreeNodes: Array.isArray(search.genreTreeNodes) ? search.genreTreeNodes : [],
      includeFields: !xs,
    }),
    queryOptions
  )
  const isManager = React.useMemo(
    () =>
      userLicenses?.some(
        (license) => license.product === "theaDispoWebManager"
      ),
    [userLicenses]
  )
  const { extendedColumns, baseColumns, defaultColumnVisibility } =
    useProductionTableColumns()
  const columns = isManager ? extendedColumns : baseColumns
  const filterProps = { w: xs ? "100%" : 300 }
  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <ResponsiveFilterGroup.Button
              filters={{
                genreTreeNodes,
                seasonIds: storedSeasonIds,
                inactive: search.inactive,
              }}
              visible={!!showFilters}
              onChange={() => setShowFilters((showFilters) => !showFilters)}
            />
          </>
        ),
      }}
    >
      <Stack h={"100%"}>
        <Group>
          <SearchTextInput
            {...filterProps}
            value={searchString}
            onChange={(e) => updateSearch({ searchString: e.target.value })}
          />
          <ResponsiveFilterGroup
            visible={!!showFilters}
            setVisible={setShowFilters}
            filters={{
              genreTreeNodes,
              seasonIds: storedSeasonIds,
              inactive: search.inactive,
            }}
            onReset={() => {
              setStoredGenreTreeNodes(undefined)
              setStoredSeasonIds(undefined)
              updateSearch({
                genreTreeNodes: undefined,
                seasonIds: undefined,
                inactive: undefined,
              })
            }}
          >
            <GenreTreeSelect
              {...filterProps}
              placeholder={t("FilterGenre")}
              value={genreTreeNodes}
              onChange={(v) => {
                const newValue = v.length ? v : undefined
                updateSearch({ genreTreeNodes: newValue })
              }}
            />
            <SeasonMultiSelect
              {...filterProps}
              value={seasonIds}
              data={seasonFilterDto.data}
              onChange={(value) => {
                const newValue = value.length ? value : undefined
                setStoredSeasonIds(newValue)
                updateSearch({ seasonIds: newValue })
              }}
            />
            <Checkbox
              label={t("ShowInactiveProductions")}
              checked={!!search.inactive}
              onChange={(e) =>
                updateSearch({ inactive: e.target.checked ? true : undefined })
              }
            />
          </ResponsiveFilterGroup>
        </Group>
        {xs ? (
          <DefaultInfiniteList
            queryResult={queryResult}
            renderItem={({ entity: production }) => (
              <DefaultListItem>
                <Card
                  w={"100%"}
                  key={production.productionId}
                  shadow="xs"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigateToProductionId(production.productionId)
                  }
                >
                  <Highlight
                    fw={500}
                    children={production.title ?? ""}
                    highlight={searchString ?? ""}
                  />
                  <Highlight
                    children={production.identifier ?? ""}
                    highlight={searchString ?? ""}
                  />
                  {production.premiere && (
                    <Text>
                      {t("Premiere")}: {getFormattedDate(production.premiere)}
                    </Text>
                  )}
                </Card>
              </DefaultListItem>
            )}
          />
        ) : (
          <InfiniteScrollingTable
            infiniteQueryResult={queryResult}
            sortingState={sortingState}
            columnDefinitions={columns}
            id={"productions-table"}
            defaultColumnVisibility={defaultColumnVisibility}
            getRowProps={(row) => ({
              onClick: () => navigateToProductionId(row.original.productionId),
            })}
          />
        )}
      </Stack>
    </Page>
  )
}
